export type BatchListener<T> = (
  batch: string[],
  next: (rows: T[]) => void,
  fail: (err: unknown) => void,
) => () => void

export interface BatchListenHandlers<T> {
  /** The merged rows of every slot, after any slot changes. */
  onRows: (rows: T[]) => void
  /** Every slot has delivered or failed at least once. */
  onSettled: () => void
  /** `ids` is the batch that failed; `retryId` is set when a single id still failed after its batch was split; `willRetry` when this failure triggers the split. */
  onError: (err: unknown, info: { ids: string[]; retryId?: string; willRetry: boolean }) => void
}

export const isPermissionDenied = (err: unknown) => (err as { code?: string } | null)?.code === 'permission-denied'

// A query is all-or-nothing: one id the rules deny (a deleted or draft doc)
// fails its whole batch. A denied batch is retried id by id so only the
// offending ids go missing instead of every row in the batch.
export function listenInBatches<T>(
  batches: string[][],
  listen: BatchListener<T>,
  handlers: BatchListenHandlers<T>,
): () => void {
  const slotRows: Record<string, T[]> = {}
  const pendingSlots = new Set<string>()
  const unsubs: (() => void)[] = []
  let settled = false

  const settle = (slot: string, rows: T[]) => {
    slotRows[slot] = rows
    pendingSlots.delete(slot)
    handlers.onRows(Object.values(slotRows).flat())
    if (!settled && pendingSlots.size === 0) {
      settled = true
      handlers.onSettled()
    }
  }

  const subscribe = (slot: string, batch: string[], isRetry: boolean) => {
    unsubs.push(
      listen(
        batch,
        rows => settle(slot, rows),
        err => {
          const willRetry = !isRetry && batch.length > 1 && isPermissionDenied(err)
          handlers.onError(err, { ids: batch, retryId: isRetry ? batch[0] : undefined, willRetry })
          if (!willRetry) return settle(slot, [])
          delete slotRows[slot]
          pendingSlots.delete(slot)
          batch.forEach(id => pendingSlots.add(`${slot}:${id}`))
          batch.forEach(id => subscribe(`${slot}:${id}`, [id], true))
        },
      ),
    )
  }

  // Every slot is pending before any listener starts, so one that delivers
  // synchronously can't report the whole set settled early.
  batches.forEach((_, idx) => pendingSlots.add(String(idx)))
  batches.forEach((batch, idx) => subscribe(String(idx), batch, false))
  return () => unsubs.forEach(u => u())
}
